import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 120000,
});

// ----- Auth: bearer token stored in localStorage -----
const TOKEN_KEY = "screen_app_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY) || "";
export const setToken = (t) => {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// On 401, wipe token and force the gate to re-render.
// The AuthContext listens for 'auth:unauthorized' to reset state.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearToken();
      window.dispatchEvent(new Event("auth:unauthorized"));
    }
    return Promise.reject(err);
  }
);

export const login = async (password) => {
  const { data } = await api.post("/auth/login", { password });
  setToken(data.token);
  return data;
};

export const verifyToken = async () => {
  try {
    const { data } = await api.get("/auth/verify");
    return !!data?.valid;
  } catch {
    return false;
  }
};

export const logout = () => {
  clearToken();
  window.dispatchEvent(new Event("auth:unauthorized"));
};

export const extractFromPhoto = async (files) => {
  const form = new FormData();
  const list = Array.isArray(files) ? files : [files];
  list.forEach((f) => form.append("files", f));
  const { data } = await api.post("/extract", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const listScreenTypes = async () => {
  const { data } = await api.get("/screen-types");
  return data;
};

export const updateScreenType = async (key, payload) => {
  // payload: { tiers, cut_width_offset_mm, cut_height_offset_mm }
  const { data } = await api.put(`/screen-types/${key}`, payload);
  return data;
};

export const deleteScreenType = async (key) => {
  const { data } = await api.delete(`/screen-types/${key}`);
  return data;
};

export const createScreenType = async (label) => {
  const { data } = await api.post("/screen-types", { label });
  return data;
};

export const listQuotes = async (query) => {
  const { data } = await api.get("/quotes", {
    params: query ? { q: query } : undefined,
  });
  return data;
};

export const previewQuote = async (payload) => {
  const { data } = await api.post("/quotes/preview", payload);
  return data;
};

export const saveQuote = async (payload) => {
  const { data } = await api.post("/quotes", payload);
  return data;
};

export const getQuote = async (id) => {
  const { data } = await api.get(`/quotes/${id}`);
  return data;
};

export const deleteQuote = async (id) => {
  const { data } = await api.delete(`/quotes/${id}`);
  return data;
};

export const updateRoom = async (quoteId, roomId, patch) => {
  const { data } = await api.patch(`/quotes/${quoteId}/rooms/${roomId}`, patch);
  return data;
};

// Update top-level quote meta (client name, phone, area, colour)
export const updateQuoteMeta = async (quoteId, patch) => {
  const { data } = await api.patch(`/quotes/${quoteId}`, patch);
  return data;
};

// Per-quote MN-XXX override. Pass `null` to clear the override.
export const setQuoteCustomerNumber = async (quoteId, customerNumber) => {
  const { data } = await api.patch(`/quotes/${quoteId}/customer-number`, {
    customer_number: customerNumber,
  });
  return data;
};

// Suggest the next free MN integer + label so the editor can pre-fill it.
export const getNextCustomerNumber = async () => {
  const { data } = await api.get(`/customers/next-number`);
  return data;
};

// Append a photo (data URL) to an existing quote.
export const addQuotePhoto = async (quoteId, photoDataUrl) => {
  const { data } = await api.post(`/quotes/${quoteId}/photos`, {
    photo_data_url: photoDataUrl,
  });
  return data;
};

// Remove a photo by index from a quote.
export const removeQuotePhoto = async (quoteId, index) => {
  const { data } = await api.delete(`/quotes/${quoteId}/photos/${index}`);
  return data;
};

export const issueInvoice = async (quoteId) => {
  const { data } = await api.post(`/quotes/${quoteId}/issue-invoice`);
  return data;
};

// Returns the absolute URL to the invoice PDF endpoint (with bearer auth).
// Caller fetches with axios so the auth header is attached.
export const getInvoicePdfBlob = async (quoteId) => {
  const res = await api.get(`/quotes/${quoteId}/invoice.pdf`, {
    responseType: "blob",
  });
  return res.data; // Blob
};

// ---- Admin: backup + restore ----
export const getBackupPreview = async () => {
  const { data } = await api.get(`/admin/backup/preview`);
  return data;
};

// ---- Customer directory lookup (by number, e.g. for schedule pre-fill) ----
export const lookupCustomerByNumber = async (numberOrLabel) => {
  const { data } = await api.get(
    `/customers/by-number/${encodeURIComponent(numberOrLabel)}`
  );
  return data;
};

// ----- Colour options (per-room price multiplier) -----
export const listColorOptions = async () => {
  const { data } = await api.get("/color-options");
  return data;
};

export const createColorOption = async (label, multiplier_pct = 0) => {
  const { data } = await api.post("/color-options", { label, multiplier_pct });
  return data;
};

export const updateColorOption = async (key, payload) => {
  const { data } = await api.put(`/color-options/${key}`, payload);
  return data;
};

export const deleteColorOption = async (key) => {
  const { data } = await api.delete(`/color-options/${key}`);
  return data;
};

// ---- Day Plans (daily work schedules) ----
export const listDayPlans = async () => {
  const { data } = await api.get(`/day-plans`);
  return data;
};

export const getDayPlan = async (date) => {
  const { data } = await api.get(`/day-plans/${encodeURIComponent(date)}`);
  return data;
};

export const upsertDayPlan = async (date, plan) => {
  const { data } = await api.put(`/day-plans/${encodeURIComponent(date)}`, plan);
  return data;
};

export const deleteDayPlan = async (date) => {
  const { data } = await api.delete(`/day-plans/${encodeURIComponent(date)}`);
  return data;
};

export const getDayPlanPdfBlob = async (date) => {
  const res = await api.get(`/day-plans/${encodeURIComponent(date)}/pdf`, {
    responseType: "blob",
  });
  return res.data; // Blob
};

// ---- Packing Lists (workshop-internal) ----
export const listPackingLists = async () => {
  const { data } = await api.get(`/packing-lists`);
  return data;
};

export const getPackingList = async (date) => {
  const { data } = await api.get(`/packing-lists/${encodeURIComponent(date)}`);
  return data;
};

export const upsertPackingList = async (date, body) => {
  const { data } = await api.put(
    `/packing-lists/${encodeURIComponent(date)}`,
    body
  );
  return data;
};

export const deletePackingList = async (date) => {
  const { data } = await api.delete(
    `/packing-lists/${encodeURIComponent(date)}`
  );
  return data;
};

export const getPackingListPdfBlob = async (date) => {
  const res = await api.get(
    `/packing-lists/${encodeURIComponent(date)}/pdf`,
    { responseType: "blob" }
  );
  return res.data;
};

export const lookupPackingInfo = async (numberOrLabel) => {
  const { data } = await api.get(
    `/packing-lists/lookup/${encodeURIComponent(numberOrLabel)}`
  );
  return data;
};

export const downloadBackupBlob = async () => {
  const res = await api.get(`/admin/backup`, { responseType: "blob" });
  return res.data; // Blob
};

export const restoreBackup = async (payload, mode = "merge") => {
  const { data } = await api.post(`/admin/restore`, { mode, payload });
  return data;
};

// Workshop cut-sheet PDF (no prices). Owner-side — bearer auth attached.
export const getCutSheetPdfBlob = async (quoteId) => {
  const res = await api.get(`/quotes/${quoteId}/cut-sheet.pdf`, {
    responseType: "blob",
  });
  return res.data; // Blob
};

export const toggleCutCheck = async (quoteId, key, done) => {
  const { data } = await api.patch(`/quotes/${quoteId}/cut-check`, { key, done });
  return data;
};

export const resetCutChecks = async (quoteId) => {
  const { data } = await api.post(`/quotes/${quoteId}/cut-check/reset`);
  return data;
};

export const confirmOrder = async (quoteId) => {
  const { data } = await api.post(`/quotes/${quoteId}/confirm`);
  return data;
};

export const unconfirmOrder = async (quoteId) => {
  const { data } = await api.post(`/quotes/${quoteId}/unconfirm`);
  return data;
};

export const markPayment = async (quoteId, kind, amount) => {
  const { data } = await api.post(`/quotes/${quoteId}/payment`, { kind, amount });
  return data;
};

export const clearPayment = async (quoteId, kind) => {
  const { data } = await api.post(`/quotes/${quoteId}/payment/clear`, { kind });
  return data;
};

export const getAppSettings = async () => {
  const { data } = await api.get("/settings");
  return data;
};

export const updateAppSettings = async (payload) => {
  const { data } = await api.put("/settings", payload);
  return data;
};

// ----- Customers -----
export const listCustomers = async () => {
  const { data } = await api.get("/customers");
  return data;
};

export const getCustomerQuotes = async (name, override = null) => {
  const params = override !== null && override !== undefined ? { override } : {};
  const { data } = await api.get(`/customers/${encodeURIComponent(name)}`, {
    params,
  });
  return data;
};

// ----- Workshop public link -----
export const getWorkshopLink = async (quoteId) => {
  const { data } = await api.get(`/quotes/${quoteId}/workshop-link`);
  return data; // { quote_id, token }
};

// Public (no-auth) calls use a plain axios instance so the bearer interceptor
// doesn't attach the owner token — these endpoints authenticate via the path token.
const publicApi = axios.create({ baseURL: API, timeout: 60000 });

export const getPublicWorkshopQuote = async (quoteId, token) => {
  const { data } = await publicApi.get(`/public/workshop/${quoteId}/${token}`);
  return data;
};

export const togglePublicWorkshopCutCheck = async (quoteId, token, key, done) => {
  const { data } = await publicApi.patch(
    `/public/workshop/${quoteId}/${token}/cut-check`,
    { key, done }
  );
  return data;
};

// Public cut-sheet PDF URL (token-verified, no bearer needed).
export const getPublicCutSheetPdfUrl = (quoteId, token) =>
  `${API}/public/workshop/${quoteId}/${token}/cut-sheet.pdf`;

// ----- Schedule cut date (owner side) -----
export const setQuoteSchedule = async (quoteId, date) => {
  const { data } = await api.patch(`/quotes/${quoteId}/schedule`, { date });
  return data;
};

export const listScheduledQuotes = async (date) => {
  const { data } = await api.get(`/quotes/scheduled/${date}`);
  return data;
};

export const getScheduleDayLink = async (date) => {
  const { data } = await api.get(`/schedule/day/${date}/link`);
  return data; // { date, token }
};

// ----- Public workshop DAY view (no auth) -----
export const getPublicWorkshopDay = async (date, token) => {
  const { data } = await publicApi.get(`/public/workshop/day/${date}/${token}`);
  return data; // { date, quotes: [...] }
};

export const togglePublicWorkshopDayCutCheck = async (
  date,
  token,
  quoteId,
  key,
  done
) => {
  const { data } = await publicApi.patch(
    `/public/workshop/day/${date}/${token}/cut-check`,
    { quote_id: quoteId, key, done }
  );
  return data;
};
